import { request } from '../../../utils/api';
import {
  FETCH_ROLES,
  ADD_ROLE,
  DELETE_ROLE,
  FETCH_ROLE,
  UPDATE_ROLE,
} from '../../../constants/endpoints';
import { showToast } from '../toasts';
import actionTypes from '../../../constants/actions';
// API returns: { status, result: { roles, totalCount, limit, offset, hasMore } }

const fetchRolesStarted = () => ({
  type: actionTypes.FETCH_ROLES,
  status: 'started',
});

const fetchRolesError = () => ({
  type: actionTypes.FETCH_ROLES,
  status: 'started',
});

const fetchRolesSuccess = (data) => (dispatch) =>
  dispatch({
    type: actionTypes.FETCH_ROLES,
    status: 'success',
    data: data,
  });

const addRoleStarted = () => ({
  type: actionTypes.ADD_ROLE,
  status: 'started',
});

const addRoleError = () => ({
  type: actionTypes.ADD_ROLE,
  status: 'started',
});

const addRoleSuccess = () => (dispatch) =>
  dispatch({
    type: actionTypes.ADD_ROLE,
    status: 'success',
  });

const deleteRoleStarted = () => ({
  type: actionTypes.DELETE_ROLE,
  status: 'started',
});

const deleteRoleError = () => ({
  type: actionTypes.DELETE_ROLE,
  status: 'started',
});

const deleteRoleSuccess = () => (dispatch) =>
  dispatch({
    type: actionTypes.DELETE_ROLE,
    status: 'success',
  });

const fetchRoleStarted = () => ({
  type: actionTypes.FETCH_ROLE,
  status: 'started',
});

const fetchRoleError = () => ({
  type: actionTypes.FETCH_ROLE,
  status: 'error',
});

const fetchRoleSuccess = (data) => (dispatch) =>
  dispatch({
    type: actionTypes.FETCH_ROLE,
    status: 'success',
    data: data,
  });

const updateRoleStarted = () => ({
  type: actionTypes.UPDATE_ROLE,
  status: 'started',
});

const updateRoleError = () => ({
  type: actionTypes.UPDATE_ROLE,
  status: 'error',
});

const updateRoleSuccess = () => (dispatch) =>
  dispatch({
    type: actionTypes.UPDATE_ROLE,
    status: 'success',
  });

export const fetchRoles = () => async (dispatch) => {
  try {
    dispatch(fetchRolesStarted());
    const response = await request({
      route: FETCH_ROLES(),
      data: {},
      method: 'get',
      dispatch,
    });

    // Handle both response structures:
    // 1. { status: 200, result: { roles: [...], ... } }
    // 2. { roles: [...], totalCount: ..., ... } (direct structure)
    const result = response?.result || response;
    const roles = (result?.roles || []).sort((a, b) => {
      const nameA = a.name?.toLowerCase();
      const nameB = b.name?.toLowerCase();
      if (nameA === 'student') return -1;
      if (nameB === 'student') return 1;
      if (nameA === 'educator') return -1;
      if (nameB === 'educator') return 1;
      return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    });
    const totalPermissions = result?.totalPermissionsCount || 0;

    const data = {
      roles,
      totalPermissions,
      totalCount: result?.totalCount || 0,
      limit: result?.limit || 0,
      offset: result?.offset || 0,
      hasMore: result?.hasMore || false,
    };

    dispatch(fetchRolesSuccess(data));
    return data;
  } catch (e) {
    console.error('Fetch roles error:', e);
    let errorMessage = 'An error occurred';

    // Handle axios errors
    if (e?.response) {
      // Server responded with error status
      errorMessage =
        e.response.data?.message ||
        e.response.data ||
        e.message ||
        'Server error';
    } else if (e?.request) {
      // Request was made but no response received (network error)
      errorMessage =
        'Network error: Unable to connect to server. Please check if the backend is running.';
    } else {
      // Error setting up the request
      errorMessage = e?.message || 'Failed to fetch roles';
    }

    dispatch(
      showToast({
        message: errorMessage,
      }),
    );
    dispatch(fetchRolesError());
    // Don't re-throw to prevent uncaught errors in UI
    return null;
  }
};

export const addRole = (data) => async (dispatch) => {
  try {
    dispatch(addRoleStarted());
    const response = await request({
      route: ADD_ROLE(),
      data: data,
      method: 'post',
      dispatch,
    });

    // Check if response indicates validation error (backend returns status 400 in response body)
    if (response?.status === 400) {
      const errorMessage = response?.result?.message || 'Validation failed';
      dispatch(
        showToast({
          message: errorMessage,
        }),
      );
      dispatch(addRoleError());
      return null;
    }

    dispatch(addRoleSuccess());
    dispatch(
      showToast({
        message: 'Role added successfully',
      }),
    );
    // Refresh roles list after adding
    dispatch(fetchRoles());
    return data;
  } catch (e) {
    console.error('Add role error:', e);
    let errorMessage = 'An error occurred';

    // Handle axios errors
    if (e?.response) {
      // Server responded with error status
      // Check for validation error in response body
      const responseData = e.response.data;
      if (responseData?.result?.message) {
        errorMessage = responseData.result.message;
      } else {
        errorMessage =
          responseData?.message || responseData || e.message || 'Server error';
      }
    } else if (e?.request) {
      // Request was made but no response received (network error)
      errorMessage =
        'Network error: Unable to connect to server. Please check if the backend is running.';
    } else {
      // Error setting up the request
      errorMessage = e?.message || 'Failed to add role';
    }

    dispatch(
      showToast({
        message: errorMessage,
      }),
    );
    dispatch(addRoleError());
    // Don't re-throw to prevent uncaught errors in UI
    return null;
  }
};

export const fetchRole = (roleId) => async (dispatch) => {
  try {
    dispatch(fetchRoleStarted());
    const response = await request({
      route: FETCH_ROLE(roleId),
      data: {},
      method: 'get',
      dispatch,
    });

    if (response) {
      const roleData = response.role || response;

      const normalizedRoleData = {
        id: roleData._id || roleData.id,
        title: roleData.name || roleData.title,
        name: roleData.name || roleData.title,
        description: roleData.description || '',
        permissions: Array.isArray(roleData.permissions)
          ? roleData.permissions
          : [],
        // Ensure fallbackRole is always present so Edit/View screens can display it
        fallbackRole: roleData.fallbackRole || 'educator',
      };

      console.log('Fetched role data:', normalizedRoleData);

      dispatch(fetchRoleSuccess(normalizedRoleData));
      return normalizedRoleData;
    }

    // ✅ Added fallback in case `response` is falsy
    return null;
  } catch (e) {
    console.error('Fetch role error:', e);
    let errorMessage = 'An error occurred';

    if (e?.response) {
      const responseData = e.response.data;
      if (responseData?.result?.message) {
        errorMessage = responseData.result.message;
      } else {
        errorMessage =
          responseData?.message || responseData || e.message || 'Server error';
      }
    } else if (e?.request) {
      errorMessage =
        'Network error: Unable to connect to server. Please check if the backend is running.';
    } else {
      errorMessage = e?.message || 'Failed to fetch role';
    }

    dispatch(showToast({ message: errorMessage }));
    dispatch(fetchRoleError());
    return null;
  }
};

export const updateRole = (roleId, data) => async (dispatch) => {
  try {
    dispatch(updateRoleStarted());
    const response = await request({
      route: UPDATE_ROLE(roleId),
      data: data,
      method: 'put',
      dispatch,
    });

    if (response?.status === 400) {
      const errorMessage = response?.result?.message || 'Validation failed';
      dispatch(
        showToast({
          message: errorMessage,
        }),
      );
      dispatch(updateRoleError());
      return null;
    }

    dispatch(updateRoleSuccess());
    dispatch(
      showToast({
        message: 'Role updated successfully',
      }),
    );
    return data;
  } catch (e) {
    console.error('Add role error:', e);
    let errorMessage = 'An error occurred';

    // Handle axios errors
    if (e?.response) {
      // Server responded with error status
      // Check for validation error in response body
      const responseData = e.response.data;
      if (responseData?.result?.message) {
        errorMessage = responseData.result.message;
      } else {
        errorMessage =
          responseData?.message || responseData || e.message || 'Server error';
      }
    } else if (e?.request) {
      // Request was made but no response received (network error)
      errorMessage =
        'Network error: Unable to connect to server. Please check if the backend is running.';
    } else {
      // Error setting up the request
      errorMessage = e?.message || 'Failed to update role';
    }

    dispatch(
      showToast({
        message: errorMessage,
      }),
    );
    dispatch(updateRoleError());
    // Don't re-throw to prevent uncaught errors in UI
    return null;
  }
};

export const deleteRole = (roleId) => async (dispatch) => {
  try {
    dispatch(deleteRoleStarted());
    const response = await request({
      route: DELETE_ROLE(roleId),
      data: {},
      method: 'delete',
      dispatch,
    });

    // Check if response indicates error (backend returns status in response body)
    if (response?.status === 404) {
      const errorMessage = response?.result?.message || 'Role not found';
      dispatch(
        showToast({
          message: errorMessage,
        }),
      );
      dispatch(deleteRoleError());
      return null;
    }

    dispatch(deleteRoleSuccess());
    dispatch(
      showToast({
        message: 'Role deleted successfully',
      }),
    );
    // Refresh roles list after deletion
    dispatch(fetchRoles());
    return { success: true };
  } catch (e) {
    console.error('Delete role error:', e);
    let errorMessage = 'An error occurred';

    // Handle axios errors
    if (e?.response) {
      // Server responded with error status
      const responseData = e.response.data;
      // Check for specific error messages from backend
      if (responseData?.result?.message) {
        errorMessage = responseData.result.message;
      } else if (responseData?.message) {
        errorMessage = responseData.message;
      } else {
        errorMessage = responseData || e.message || 'Server error';
      }
    } else if (e?.request) {
      // Request was made but no response received (network error)
      errorMessage =
        'Network error: Unable to connect to server. Please check if the backend is running.';
    } else {
      // Error setting up the request
      errorMessage = e?.message || 'Failed to delete role';
    }

    dispatch(
      showToast({
        message: errorMessage,
      }),
    );
    dispatch(deleteRoleError());
    // Don't re-throw to prevent uncaught errors in UI
    return null;
  }
};
